import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import TaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { Option } from '@nxsys/types/models/Common';
import { NowPensionPayrollNoColumn } from '@nxsys/store/slices/report/types';
import { getRangeYear } from '@nxsys/utils/taxYear';
import { Form, FormProps, Space } from 'antd';
import { orderBy } from 'lodash';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import NestExportSettings from './NestExportSettings';

type ExportPensionFormProps = {
  title: string;
  onExport: (values: ExportPensionFormValues) => Promise<void>;
  /** Show NOW Pension–specific settings: "Include all paid employees" + PAYROLLNO column. */
  showNowPensionSettings?: boolean;
  /** Show NEST contribution header fields (start/end/due date, payment source, frequency). */
  showNestSettings?: boolean;
  /** Bureau-mode Payment Company filter — pension providers to include.
   * Defaults to the NOW-like providers; the NEST card passes NEST_PENSION. */
  companyPensionProviderFilter?: string[];
};

export type ExportPensionFormValues = {
  payFrequency: string;
  taxYear: number;
  taxPeriod: number;
  company: Option<string> | null;
  /** NOW Pension only — include all paid employees regardless of assessment status. */
  includeAllPaid?: boolean;
  /** NOW Pension only — which value to fill into the PAYROLLNO column. */
  payrollNoSource?: { label: string, value: NowPensionPayrollNoColumn };
  /** NEST only — user-entered H-line header values (dates as ISO YYYY-MM-DD). */
  nestStartDate?: string;
  nestEndDate?: string;
  nestDueDate?: string;
  nestPaymentSource?: string;
  nestFrequency?: string;
};

const ExportPensionForm: React.FC<ExportPensionFormProps> = ({
  title,
  onExport,
  showNowPensionSettings = false,
  showNestSettings = false,
  companyPensionProviderFilter,
}) => {
  const { messages } = useTypedIntl();
  const { isBureau, user, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const taxYearRange = orderBy(getRangeYear(isIreland), 'value', 'desc');
  const [form] = Form.useForm();
  const watchPayFrequency = Form.useWatch('payFrequency', form);
  const watchTaxPeriod = Form.useWatch('taxPeriod', form);
  const watchTaxYear = Form.useWatch('taxYear', form);
  const watchCompany = Form.useWatch('company', form);

  // NEST export gating: all header fields must be present.
  const watchNestStart = Form.useWatch('nestStartDate', form);
  const watchNestEnd = Form.useWatch('nestEndDate', form);
  const watchNestDue = Form.useWatch('nestDueDate', form);
  const watchNestSource = Form.useWatch('nestPaymentSource', form);
  const watchNestFrequency = Form.useWatch('nestFrequency', form);
  const nestExportBlocked =
    showNestSettings &&
    !(
      watchNestStart &&
      watchNestEnd &&
      watchNestDue &&
      watchNestSource &&
      watchNestFrequency
    );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onFinish: FormProps['onFinish'] = (values) => {
    setIsSubmitting(true);
    onExport(values).finally(() => setIsSubmitting(false));
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppCard>
      <AppsHeader>
        <Space align="center">
          <div className="rounded-[6px] p-[8px] border-solid border-neutral-100 border-[1px]">
            <Icons.IconFileText />
          </div>
          <div>{title}</div>
        </Space>
      </AppsHeader>
      <AppsContent>
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="p-[16px]"
          initialValues={{
            taxYear: taxYearRange[0].value,
            ...(showNowPensionSettings
              ? {
                  includeAllPaid: false,
                  payrollNoSource: 'EMPLOYEE_ID',
                }
              : {}),
          }}
        >
          <Form.Item
            label={messages['common.payFrequency']}
            name="payFrequency"
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppPayFrequencySelect
              placeholder={messages['form.select']}
              onChange={(value) => {
                form.setFieldsValue({ payFrequency: value || null });
                form.setFieldsValue({ taxPeriod: null });
              }}
              value={watchPayFrequency}
            />
          </Form.Item>

          <Form.Item
            label={messages['common.taxYear']}
            name="taxYear"
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <TaxYearSelect
              placeholder={messages['form.select']}
              onFilter={(value) => {
                form.setFieldsValue({ taxYear: value.taxYear });
              }}
            />
          </Form.Item>

          <Form.Item
            label={messages['common.taxPeriod']}
            name="taxPeriod"
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <TaxWeekMonthSelect
              placeholder={messages['form.select']}
              onFilter={(value) => {
                form.setFieldsValue({ taxPeriod: value.taxPeriod });
              }}
              value={watchTaxPeriod}
              paymentFrequency={watchPayFrequency}
              disabled={!watchPayFrequency}
            />
          </Form.Item>

          <Form.Item
            label={
              isBureau
                ? messages['common.employer']
                : messages['common.paymentCompany']
            }
            name="company"
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppSelectSync
              type="company"
              apiType={isBureau ? 'searchBureauEmployer' : 'searchCompany'}
              paramFilter={{
                pensionProviderId: companyPensionProviderFilter || undefined,
                companyIds: user?.companyUserIds || undefined,
              }}
              placeholder={messages['form.pleaseSelect']}
              onChangeValue={(value) => {
                form.setFieldsValue({
                  company: value,
                });
              }}
            />
          </Form.Item>

          {showNowPensionSettings && (
            <>
              <Form.Item
                label={
                  messages['common.export.includeAllPaidEmployees'] ||
                  'Include all paid employees?'
                }
                name="includeAllPaid"
                rules={[
                  { required: true, message: messages['form.pleaseSelect'] },
                ]}
              >
                <AppRadioGroup
                  options={[
                    { label: messages['common.yes'] || 'Yes', value: true },
                    { label: messages['common.no'] || 'No', value: false },
                  ]}
                />
              </Form.Item>

              <Form.Item
                label={
                  messages['common.export.payrollNoSource'] ||
                  'Fill PAYROLLNO column with'
                }
                name="payrollNoSource"
                rules={[
                  { required: true, message: messages['form.pleaseSelect'] },
                ]}
              >
                <AppSelect
                  className="w-full !min-w-0"
                  placeholder={messages['form.select']}
                  options={[
                    {
                      label: messages['common.employeeId'] || 'Employee ID',
                      value: 'EMPLOYEE_ID',
                    },
                    {
                      label: isBureau
                        ? messages['common.employerRef'] || 'Employer Ref'
                        : messages['common.agencyRef'] || 'Agency Ref',
                      value: 'EMPLOYER_REF',
                    },
                    {
                      label: messages['common.membershipId'] || 'Membership ID',
                      value: 'MEMBERSHIP_ID',
                    },
                  ]}
                />
              </Form.Item>
            </>
          )}

          {showNestSettings &&
            watchPayFrequency &&
            watchTaxYear &&
            watchTaxPeriod &&
            watchCompany?.value && (
              <NestExportSettings
                form={form}
                companyId={watchCompany?.value}
                payFrequency={watchPayFrequency}
                taxPeriod={watchTaxPeriod}
                taxYear={watchTaxYear}
              />
            )}

          <AppButton
            name={messages['common.export']}
            variant="outlined"
            onClick={() => form.submit()}
            className="w-full"
            loading={isSubmitting}
            disabled={isSubmitting || nestExportBlocked}
          />
        </AppFormContainer>
      </AppsContent>
    </AppCard>
  );
};

export default ExportPensionForm;
